import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import ImageUploadButton from '../meal/image_upload_button';
import imgPlaceholder from '../../assets/images/imgPlaceholder.png';
import { uploadUserImage } from '../../services/user_generated_content';
import { MembershipTierState } from '../../API';
import UnsavedChangesWarningPopover from '../common/unsaved_changes_warning_popover';
import { useGetMembershipTiersBySpaceQuery } from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetEndUserSpaceQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { userIsCreator } from '../../services/users';

const RecipesBoardEditModal = ({ initialBoard, onSave, onDismiss }) => {
  const [title, setTitle] = useState(initialBoard.title);
  const [coverImageUrl, setCoverImageUrl] = useState(
    initialBoard.coverImageUrl,
  );
  const [shortDescription, setShortDescription] = useState(
    initialBoard.shortDescription,
  );
  const [imageObjectId] = useState(uuidv4());
  const [availableInMembershipTierIDs, setAvailableInMembershipTierIDs] =
    useState(initialBoard.availableInMembershipTierIDs || []);

  const [unsavedChangesWarningVisible, setUnsavedChangesWarningVisible] =
    useState(false);

  const [unsavedChangesWarningAnchorEl, setUnsavedChangesWarningAnchorEl] =
    useState(null);

  const onConfirmClose = () => {
    onDismiss();
  };

  const onDialogClose = (event, isClickAway) => {
    console.log({ isDirty });
    if (isDirty) {
      event.preventDefault();
      setUnsavedChangesWarningVisible(true);
      setUnsavedChangesWarningAnchorEl(isClickAway ? null : event.target);
    } else {
      onConfirmClose();
    }
  };

  const onDismissUnsavedChangesWarningPopover = () => {
    setUnsavedChangesWarningAnchorEl(null);
    setUnsavedChangesWarningVisible(false);
  };
  const theme = useTheme();

  const [isDirty, setIsDirty] = useState(false);

  const onNewImageChosen = async (fileList) => {
    const newImageUrl = await uploadUserImage(imageObjectId, fileList);
    setCoverImageUrl(newImageUrl);
    setIsDirty(true);
  };

  const handleSave = () => {
    onSave({
      ...initialBoard,
      title,
      coverImageUrl,
      shortDescription,
      availableInMembershipTierIDs,
    });
    setIsDirty(false);
  };

  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpace } = useGetEndUserSpaceQuery(undefined, {
    skip: !userProfile || isSmorgStudio,
  });

  const { data: allMembershipTiers } = useGetMembershipTiersBySpaceQuery(
    currentSpace?.id,
    {
      skip: !currentSpace?.id,
    },
  );

  const membershipTiers = (allMembershipTiers || []).filter(
    (mt) => mt.state === MembershipTierState.ACTIVE,
  );

  const onChangeAvailableInMembershipTierID = (
    membershipTierID,
    isAvailable,
  ) => {
    if (
      isAvailable &&
      !availableInMembershipTierIDs?.includes(membershipTierID)
    ) {
      setAvailableInMembershipTierIDs((mtids) => [
        ...(mtids || []),
        membershipTierID,
      ]);
      setIsDirty(true);
    } else if (!isAvailable) {
      setAvailableInMembershipTierIDs((mtids) =>
        mtids?.filter((mtid) => mtid !== membershipTierID),
      );
      setIsDirty(true);
    }
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      onClose={(event) => onDialogClose(event, true)}>
      <DialogTitle className="content">
        <Box
          sx={{
            marginTop: '-15px',
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
          }}>
          <Tooltip title="Dismiss and return to board" placement="top-start">
            <IconButton
              aria-label="close"
              onClick={(event) => onDialogClose(event, false)}
              size="large">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <div style={{ marginLeft: 10 }}>Edit recipes board</div>
      </DialogTitle>
      <DialogContent>
        <Grid container>
          <Grid item xs={12}>
            <h5>Title</h5>
          </Grid>
          <Grid item xs={12} style={{ padding: '5px' }}>
            <TextField
              fullWidth
              variant="standard"
              value={title}
              autoFocus
              onChange={(ev) => {
                setTitle(ev.target.value);
                setIsDirty(true);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <h5>Board cover image</h5>
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <Grid
                item
                xs={12}
                className="mealDetailImage"
                style={{
                  position: 'relative',
                  backgroundImage: `url("${coverImageUrl || imgPlaceholder}")`,
                }}>
                <ImageUploadButton
                  style={{ position: 'absolute', bottom: 10, right: 10 }}
                  onChange={onNewImageChosen}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <h5>Short description</h5>
          </Grid>
          <Grid item xs={12}>
            <TextField
              value={shortDescription}
              onChange={(ev) => {
                setShortDescription(ev.target.value);
                setIsDirty(true);
              }}
              margin="dense"
              label="Add a short description"
              type="text"
              fullWidth
              size="small"
              variant="outlined"
              multiline
              rows={4}
            />
          </Grid>
          <Grid item xs={12}>
            <h5>Assign membership tier</h5>
          </Grid>
          <Grid item xs={12}>
            {membershipTiers.map((mt) => (
              <FormControlLabel
                key={mt.id}
                control={
                  <Checkbox
                    checked={availableInMembershipTierIDs?.includes(mt.id)}
                    onChange={(ev) => {
                      onChangeAvailableInMembershipTierID(
                        mt.id,
                        ev.target.checked,
                      );
                    }}
                  />
                }
                label={mt.title}
              />
            ))}
          </Grid>
          <Grid item xs={12} style={{ margin: '15px 0', padding: '6px' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isDirty}>
              Save
            </Button>
            <Button variant="contained" color="secondary" onClick={onDismiss}>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      {unsavedChangesWarningVisible && (
        <UnsavedChangesWarningPopover
          anchorEl={unsavedChangesWarningAnchorEl}
          onConfirmClose={onConfirmClose}
          onDismiss={onDismissUnsavedChangesWarningPopover}
        />
      )}
    </Dialog>
  );
};

RecipesBoardEditModal.propTypes = {
  initialBoard: PropTypes.shape({
    title: PropTypes.string.isRequired,
    shortDescription: PropTypes.string,
    coverImageUrl: PropTypes.string,
    availableInMembershipTierIDs: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default RecipesBoardEditModal;
